import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft, Plus, Trash2, Share2, ChevronDown, ChevronRight,
  Copy, Check, ExternalLink, Eye, Pencil, Link2, User, Archive, ArchiveRestore, LayoutGrid, ListChecks
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getFurnitureProject, updateFurnitureProject, addFurnitureRoom,
  updateFurnitureRoom, deleteFurnitureRoom, addFurnitureItem,
  updateFurnitureItem, deleteFurnitureItem,
  getFurnitureProjectByShareToken, getFurnitureProjectByViewToken,
  updateFurnitureItemViaShare,
  reorderFurnitureRooms, reorderFurnitureRoomsViaShare,
  reorderFurnitureItems, reorderFurnitureItemsViaShare,
  syncRoomDefaultLinks,
  listFurnitureReceipts, uploadFurnitureReceipt,
  updateFurnitureReceipt, deleteFurnitureReceipt,
  setFurnitureProjectArchived,
} from '@/lib/api';
import { meProject } from '@/lib/staffApi';
import { ProjectSummaryTable } from '@/components/furniture/ProjectSummaryTable';
import { RoomDetail } from '@/components/furniture/RoomDetail';
import { AddRoomDialog } from '@/components/furniture/AddRoomDialog';
import { DesignerProfileDialog } from '@/components/furniture/DesignerProfileDialog';
import { ShareDialog } from '@/components/furniture/ShareDialog';
import { BreakdownWizardDialog } from '@/components/furniture/BreakdownWizardDialog';
import { ReceiptsView } from '@/components/furniture/ReceiptsView';
import { ProjectTasksPanel } from '@/components/furniture/ProjectTasksPanel';
import { StaffHeader, StaffBottomNav } from '@/components/staff/StaffNav';
import { ConfirmDeleteDialog } from '@/components/furniture/ConfirmDeleteDialog';
import { ExportProjectButton } from '@/components/furniture/ExportProjectButton';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors
} from '@dnd-kit/core';
import {
  SortableContext, arrayMove, verticalListSortingStrategy, useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = process.env.REACT_APP_BACKEND_URL;

// Wraps a RoomDetail so it is sortable as a unit. Drag handle is exposed
// inside the RoomDetail header via dragHandleProps.
const SortableRoom = ({ id, readOnly, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: readOnly });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
  };
  return children({
    sortableRef: setNodeRef,
    sortableStyle: style,
    dragHandleProps: listeners,
    dragAttributes: attributes,
    isDragging,
  });
};

export default function FurnitureProjectDetailPage() {
  const { projectId, shareToken, viewToken } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const isShareMode = !!shareToken;
  const isViewMode = !!viewToken;
  const isStaffMode = location.pathname.startsWith('/staff/');
  const isPublicAccess = isShareMode || isViewMode;

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);
  const [nameVal, setNameVal] = useState('');
  const [addressVal, setAddressVal] = useState('');
  const [expandedRooms, setExpandedRooms] = useState({});
  const [showAddRoom, setShowAddRoom] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [showDesigner, setShowDesigner] = useState(false);
  const [activeView, setActiveView] = useState('rooms');
  const [receipts, setReceipts] = useState([]);

  const load = useCallback(async () => {
    try {
      let data;
      if (shareToken) {
        data = await getFurnitureProjectByShareToken(shareToken);
      } else if (viewToken) {
        data = await getFurnitureProjectByViewToken(viewToken);
      } else if (isStaffMode) {
        // Returns the same shape as getFurnitureProject + permission_level +
        // can_edit, with financials stripped server-side for `limited` users.
        data = await meProject(projectId);
      } else {
        data = await getFurnitureProject(projectId);
      }
      setProject(data);
      setNameVal(data.name);
      setAddressVal(data.address);
      // Receipts are only available in first-party admin/staff flows.
      // Share/view tokens don't expose them (no read endpoint for tokens).
      if (!shareToken && !viewToken) {
        try {
          const r = await listFurnitureReceipts(projectId || data.id);
          setReceipts(r.receipts || []);
        } catch { /* silent — feature just won't show data */ }
      }
    } catch {
      toast.error('Failed to load project');
    }
    setLoading(false);
  }, [projectId, shareToken, viewToken, isStaffMode]);

  useEffect(() => { load(); }, [load]);

  const saveName = async () => {
    if (!nameVal.trim() || isViewMode) return;
    try {
      await updateFurnitureProject(project.id, { name: nameVal.trim() });
      setProject(prev => ({ ...prev, name: nameVal.trim() }));
      setEditingName(false);
    } catch { toast.error('Failed to update'); }
  };

  const saveAddress = async () => {
    if (isViewMode) return;
    try {
      await updateFurnitureProject(project.id, { address: addressVal.trim() });
      setProject(prev => ({ ...prev, address: addressVal.trim() }));
      setEditingAddress(false);
    } catch { toast.error('Failed to update'); }
  };

  const handleAddRoom = async (name, roomType, items, essentials) => {
    if (isViewMode) return;
    try {
      const payload = { name, room_type: roomType };
      // If the dialog returned customised lists (always true with the new
      // AddRoomDialog), pass them so the backend skips the template.
      if (Array.isArray(items)) payload.items = items;
      if (Array.isArray(essentials)) payload.essentials = essentials;
      const room = await addFurnitureRoom(project.id, payload);
      const attached = room.default_links_attached || 0;
      // Strip the metadata before pushing into state so it doesn't leak.
      const { default_links_attached: _ignore, ...roomDoc } = room;
      setProject(prev => ({ ...prev, rooms: [...prev.rooms, roomDoc] }));
      setExpandedRooms(prev => ({ ...prev, [roomDoc.id]: true }));
      setShowAddRoom(false);
      toast.success(
        attached > 0
          ? `Room added · ${attached} default link${attached === 1 ? '' : 's'} attached`
          : 'Room added',
      );
    } catch { toast.error('Failed to add room'); }
  };

  const handleUpdateRoom = async (roomId, data) => {
    if (isViewMode) return;
    try {
      await updateFurnitureRoom(project.id, roomId, data);
      setProject(prev => ({
        ...prev,
        rooms: prev.rooms.map(r => r.id === roomId ? { ...r, ...data } : r)
      }));
    } catch { toast.error('Failed to update room'); }
  };

  const handleDeleteRoom = async (roomId) => {
    if (isViewMode) return;
    // Snapshot the room (with all its items + order) so we can offer
    // an in-session undo via the toast action below.
    const snapshot = project?.rooms?.find(r => r.id === roomId);
    try {
      await deleteFurnitureRoom(project.id, roomId);
      setProject(prev => ({
        ...prev,
        rooms: prev.rooms.filter(r => r.id !== roomId)
      }));
      if (!snapshot) { toast.success('Room deleted'); return; }
      toast('Room deleted', {
        description: snapshot.name,
        duration: 8000,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              // Re-create the room with the same name, type, and items.
              // Item / room IDs will differ but the data is identical.
              const itemNames = (snapshot.items || []).filter(i => !i.is_essential).map(i => i.name);
              const essentialNames = (snapshot.items || []).filter(i => i.is_essential).map(i => i.name);
              const restored = await addFurnitureRoom(project.id, {
                name: snapshot.name,
                room_type: snapshot.room_type || 'Custom',
                items: itemNames,
                essentials: essentialNames,
              });
              setProject(prev => ({ ...prev, rooms: [...(prev.rooms || []), restored] }));
              toast.success('Room restored');
            } catch { toast.error('Restore failed'); }
          },
        },
      });
    } catch { toast.error('Failed to delete room'); }
  };

  const handleAddItem = async (roomId, itemData) => {
    if (isViewMode) return;
    try {
      const item = await addFurnitureItem(project.id, roomId, itemData);
      setProject(prev => ({
        ...prev,
        rooms: prev.rooms.map(r =>
          r.id === roomId ? { ...r, items: [...r.items, item] } : r
        )
      }));
      // If a default-link mapping kicked in for this essential, surface it.
      if (item.is_essential && (item.links || []).length > 0) {
        toast.success(`Default link${item.links.length === 1 ? '' : 's'} attached`);
      }
    } catch { toast.error('Failed to add item'); }
  };

  const handleSyncDefaultLinks = async (roomId) => {
    if (isViewMode || isShareMode) return;
    try {
      const res = await syncRoomDefaultLinks(project.id, roomId);
      const room = res.room;
      setProject(prev => ({
        ...prev,
        rooms: prev.rooms.map(r => r.id === roomId ? { ...r, items: room.items } : r),
      }));
      if (res.attached > 0) {
        toast.success(`${res.attached} default link${res.attached === 1 ? '' : 's'} attached`);
      } else {
        toast('No new links to attach', { description: "Every essential here already has links or no mapping exists." });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Sync failed'); }
  };

  const handleUpdateItem = async (roomId, itemId, data) => {
    if (isViewMode) return;
    try {
      if (isShareMode) {
        await updateFurnitureItemViaShare(shareToken, roomId, itemId, data);
      } else {
        await updateFurnitureItem(project.id, roomId, itemId, data);
      }
      setProject(prev => ({
        ...prev,
        rooms: prev.rooms.map(r =>
          r.id === roomId
            ? { ...r, items: r.items.map(i => i.id === itemId ? { ...i, ...data } : i) }
            : r
        )
      }));
    } catch { toast.error('Failed to update item'); }
  };

  // Pending item deletion: drives the confirm dialog before we actually
  // call the server. Keyed by { roomId, itemId } so we can show the item
  // name and route the confirm action.
  const [pendingItemDelete, setPendingItemDelete] = useState(null);

  const requestDeleteItem = (roomId, itemId) => {
    if (isViewMode) return;
    const room = project?.rooms?.find(r => r.id === roomId);
    const item = room?.items?.find(i => i.id === itemId);
    if (!item) return;
    setPendingItemDelete({ roomId, itemId, name: item.name, snapshot: item });
  };

  const handleDeleteItem = async (roomId, itemId) => {
    if (isViewMode) return;
    // Snapshot item so we can offer an in-session Undo via toast.
    const room = project?.rooms?.find(r => r.id === roomId);
    const snapshot = room?.items?.find(i => i.id === itemId);
    try {
      await deleteFurnitureItem(project.id, roomId, itemId);
      setProject(prev => ({
        ...prev,
        rooms: prev.rooms.map(r =>
          r.id === roomId
            ? { ...r, items: r.items.filter(i => i.id !== itemId) }
            : r
        )
      }));
      if (!snapshot) return;
      toast('Item deleted', {
        description: snapshot.name,
        duration: 8000,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              // Re-create the item with the same name + is_essential flag.
              // Backend auto-attaches default essential links/price on restore.
              const restored = await addFurnitureItem(project.id, roomId, {
                name: snapshot.name,
                is_essential: !!snapshot.is_essential,
              });
              // Carry over the fields the add endpoint doesn't accept on
              // create so the restored row mirrors what was deleted.
              const editableFields = ['links', 'specifications', 'budget', 'listed_price', 'ordered_price', 'status', 'notes', 'image_url', 'hidden_link_urls'];
              const patch = {};
              for (const k of editableFields) if (snapshot[k] != null) patch[k] = snapshot[k];
              if (Object.keys(patch).length) {
                try { await updateFurnitureItem(project.id, roomId, restored.id, patch); } catch {}
              }
              const merged = { ...restored, ...patch };
              setProject(prev => ({
                ...prev,
                rooms: prev.rooms.map(r =>
                  r.id === roomId ? { ...r, items: [...r.items, merged] } : r
                ),
              }));
              toast.success('Item restored');
            } catch { toast.error('Restore failed'); }
          },
        },
      });
    } catch { toast.error('Failed to delete item'); }
  };

  // ── Receipts ───────────────────────────────────────────────────
  // Receipt upload is admin/staff only (no share/view token support).
  const handleUploadReceipt = async ({ file, itemIds = [], vendor = '', amount = '', note = '' }) => {
    if (isViewMode || isShareMode) return null;
    try {
      const r = await uploadFurnitureReceipt(project.id, { file, itemIds, vendor, amount, note });
      setReceipts(prev => [r, ...prev]);
      toast.success('Receipt uploaded');
      return r;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to upload receipt');
      return null;
    }
  };

  const handleUpdateReceipt = async (receiptId, data) => {
    if (isViewMode || isShareMode) return;
    try {
      const r = await updateFurnitureReceipt(project.id, receiptId, data);
      setReceipts(prev => prev.map(x => x.id === receiptId ? r : x));
      toast.success('Receipt updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update receipt');
    }
  };

  const handleDeleteReceipt = async (receiptId) => {
    if (isViewMode || isShareMode) return;
    try {
      await deleteFurnitureReceipt(project.id, receiptId);
      setReceipts(prev => prev.filter(x => x.id !== receiptId));
      toast.success('Receipt deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete receipt');
    }
  };

  const toggleRoom = (roomId) => {
    setExpandedRooms(prev => ({ ...prev, [roomId]: !prev[roomId] }));
  };

  const handleReorderItems = async (roomId, itemIds) => {
    if (isViewMode) return;
    // Optimistic local reorder
    setProject(prev => ({
      ...prev,
      rooms: prev.rooms.map(r => {
        if (r.id !== roomId) return r;
        const byId = Object.fromEntries((r.items || []).map(i => [i.id, i]));
        const reordered = itemIds.map(id => byId[id]).filter(Boolean);
        const missing = (r.items || []).filter(i => !itemIds.includes(i.id));
        return { ...r, items: [...reordered, ...missing] };
      }),
    }));
    try {
      if (isShareMode) {
        await reorderFurnitureItemsViaShare(shareToken, roomId, itemIds);
      } else {
        await reorderFurnitureItems(project.id, roomId, itemIds);
      }
    } catch {
      toast.error('Failed to save item order');
      load();
    }
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const handleReorderRooms = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = project.rooms.findIndex(r => r.id === active.id);
    const newIdx = project.rooms.findIndex(r => r.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const next = arrayMove(project.rooms, oldIdx, newIdx);
    setProject(prev => ({ ...prev, rooms: next }));
    try {
      const ids = next.map(r => r.id);
      if (isShareMode) {
        await reorderFurnitureRoomsViaShare(shareToken, ids);
      } else {
        await reorderFurnitureRooms(project.id, ids);
      }
    } catch {
      toast.error('Failed to save room order');
      load();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fbfaf7] flex items-center justify-center">
        <div className="animate-pulse text-[#5b6773]">Loading project...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-[#fbfaf7] flex items-center justify-center">
        <p className="text-[#5b6773]">Project not found</p>
      </div>
    );
  }

  // Build a per-item index so RoomDetail can show receipt counts/buttons
  // without needing to scan the full receipt list on every render.
  const receiptsByItem = {};
  for (const r of receipts) {
    for (const iid of (r.item_ids || [])) {
      if (!receiptsByItem[iid]) receiptsByItem[iid] = [];
      receiptsByItem[iid].push(r);
    }
  }

  // In staff mode, mutations are gated by the user's permission_level on the
  // server (full = edit, commenter/limited = read-only) and we mirror that on
  // the client so the UI doesn't surface affordances the user can't use.
  // For `limited` users the backend has already stripped financial fields, so
  // input boxes for those just render empty (which is exactly what the user
  // asked for: same UI, blank fields they don't have access to).
  const readOnly = isViewMode || (isStaffMode && project.can_edit === false);
  const canShare = !isPublicAccess && !isStaffMode;          // sharing = admin app only
  const canSeeDesigner = !isViewMode;

  const PageInner = (
    <div className={`min-h-screen bg-[#fbfaf7] ${isStaffMode ? 'pb-24' : ''}`}>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          {!isPublicAccess && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(isStaffMode ? '/staff/projects' : '/furniture-projects')}
              data-testid="back-to-projects"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          <div className="flex-1 min-w-0">
            {editingName && !readOnly ? (
              <Input
                value={nameVal} onChange={e => setNameVal(e.target.value)}
                onBlur={saveName} onKeyDown={e => e.key === 'Enter' && saveName()}
                autoFocus className="text-2xl font-heading font-semibold h-auto py-1"
                data-testid="edit-project-name"
              />
            ) : (
              <h1
                className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight text-[#1f2933] cursor-pointer hover:text-[#2d948c] transition-colors"
                onClick={() => !readOnly && setEditingName(true)}
                data-testid="project-name"
              >
                {project.name}
                {project.archived && <span className="text-sm font-normal text-[#8b6914] bg-[#fef3c7] border border-[#fde68a] px-2 py-0.5 rounded-full ml-2 align-middle">Archived</span>}
                {isShareMode && <span className="text-sm font-normal text-[#2d948c] ml-2">(Designer Edit)</span>}
                {isViewMode && <span className="text-sm font-normal text-[#5b6773] ml-2">(View Only)</span>}
                {isStaffMode && project.can_edit === false && <span className="text-sm font-normal text-[#5b6773] ml-2">(View Only)</span>}
              </h1>
            )}
            {editingAddress && !readOnly ? (
              <Input
                value={addressVal} onChange={e => setAddressVal(e.target.value)}
                onBlur={saveAddress} onKeyDown={e => e.key === 'Enter' && saveAddress()}
                autoFocus className="text-sm mt-1 h-auto py-0.5"
                data-testid="edit-project-address"
              />
            ) : (
              <p
                className="text-sm text-[#5b6773] mt-0.5 cursor-pointer hover:text-[#2d948c] transition-colors"
                onClick={() => !readOnly && setEditingAddress(true)}
                data-testid="project-address"
              >
                {project.address || (readOnly ? 'No address' : 'Click to add address')}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {canSeeDesigner && (
              <Button variant="outline" size="sm" onClick={() => setShowDesigner(true)} className="gap-1.5" data-testid="designer-profile-btn">
                <User className="h-4 w-4" /> Designer Profile
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (isShareMode) navigate(`/furniture-projects/share/${shareToken}/project-view`);
                else if (isViewMode) navigate(`/furniture-projects/view/${viewToken}/project-view`);
                else if (typeof window !== 'undefined' && window.location.pathname.startsWith('/staff/')) {
                  navigate(`/staff/projects/${project.id}/project-view`);
                }
                else navigate(`/furniture-projects/${project.id}/project-view`);
              }}
              className="gap-1.5"
              data-testid="view-project-btn"
            >
              <LayoutGrid className="h-4 w-4" /> View Project
            </Button>
            {canShare && (
              <Button variant="outline" size="sm" onClick={() => setShowBreakdown(true)} className="gap-1.5" data-testid="cost-breakdown-btn">
                <ListChecks className="h-4 w-4" /> Cost Breakdown
              </Button>
            )}
            {canShare && (
              <Button variant="outline" size="sm" onClick={() => setShowShare(true)} className="gap-1.5" data-testid="share-project-btn">
                <Share2 className="h-4 w-4" /> Share
              </Button>
            )}
            {isPublicAccess && shareToken && (
              <ExportProjectButton shareToken={shareToken} />
            )}
            {!readOnly && !isPublicAccess && (
              <Button
                variant="outline" size="sm"
                onClick={async () => {
                  const nextArchived = !project.archived;
                  if (nextArchived && !window.confirm('Archive this project? It will be hidden from the main list but stays fully accessible.')) return;
                  try {
                    await setFurnitureProjectArchived(project.id, nextArchived);
                    setProject(prev => ({ ...prev, archived: nextArchived, archived_at: nextArchived ? new Date().toISOString() : null }));
                    toast.success(nextArchived ? 'Project archived' : 'Project unarchived');
                  } catch { toast.error('Failed to update'); }
                }}
                className="gap-1.5"
                data-testid="archive-project-btn"
              >
                {project.archived
                  ? <><ArchiveRestore className="h-4 w-4" /> Unarchive</>
                  : <><Archive className="h-4 w-4" /> Archive</>}
              </Button>
            )}
            {!readOnly && (
              <Button size="sm" onClick={() => setShowAddRoom(true)} className="gap-1.5" data-testid="add-room-btn">
                <Plus className="h-4 w-4" /> Add Room
              </Button>
            )}
          </div>
        </div>

        {/* View Toggle */}
        <div className="mb-6">
          <Tabs value={activeView} onValueChange={setActiveView}>
            <TabsList data-testid="view-toggle">
              <TabsTrigger value="summary" data-testid="view-summary-tab">Project Summary</TabsTrigger>
              <TabsTrigger value="rooms" data-testid="view-rooms-tab">Room Details</TabsTrigger>
              {!isPublicAccess && (
                <TabsTrigger value="tasks" data-testid="view-tasks-tab">Tasks &amp; Notes</TabsTrigger>
              )}
              {!isPublicAccess && (
                <TabsTrigger value="receipts" data-testid="view-receipts-tab">
                  Receipts{receipts.length > 0 ? ` (${receipts.length})` : ''}
                </TabsTrigger>
              )}
            </TabsList>
          </Tabs>
        </div>

        {/* Summary View */}
        {activeView === 'summary' && (
          <ProjectSummaryTable
            rooms={project.rooms}
            onRoomClick={(roomId) => {
              setActiveView('rooms');
              setExpandedRooms(prev => ({ ...prev, [roomId]: true }));
            }}
          />
        )}

        {/* Rooms View */}
        {activeView === 'rooms' && (
          <div className="space-y-4">
            {project.rooms.length === 0 ? (
              <Card className="border-dashed border-2 bg-transparent">
                <CardContent className="py-12 text-center">
                  <p className="text-[#5b6773] mb-3">No rooms yet. Add a room to get started.</p>
                  {!readOnly && (
                    <Button variant="outline" onClick={() => setShowAddRoom(true)} className="gap-1.5">
                      <Plus className="h-4 w-4" /> Add Room
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleReorderRooms}>
                <SortableContext items={project.rooms.map(r => r.id)} strategy={verticalListSortingStrategy}>
                  {project.rooms.map(room => (
                    <SortableRoom key={room.id} id={room.id} readOnly={readOnly}>
                      {(sortableProps) => (
                        <RoomDetail
                          room={room}
                          expanded={!!expandedRooms[room.id]}
                          onToggle={() => toggleRoom(room.id)}
                          onUpdateRoom={(data) => handleUpdateRoom(room.id, data)}
                          onDeleteRoom={() => handleDeleteRoom(room.id)}
                          onAddItem={(data) => handleAddItem(room.id, data)}
                          onUpdateItem={(itemId, data) => handleUpdateItem(room.id, itemId, data)}
                          onDeleteItem={(itemId) => requestDeleteItem(room.id, itemId)}
                          onReorderItems={handleReorderItems}
                          onSyncDefaultLinks={isShareMode || isViewMode ? null : () => handleSyncDefaultLinks(room.id)}
                          readOnly={readOnly}
                          projectId={project.id}
                          shareToken={shareToken}
                          viewToken={viewToken}
                          receiptsByItem={receiptsByItem}
                          onUploadReceipt={!isPublicAccess ? handleUploadReceipt : null}
                          {...sortableProps}
                        />
                      )}
                    </SortableRoom>
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </div>
        )}

        {/* Receipts View */}
        {activeView === 'receipts' && !isPublicAccess && (
          <ReceiptsView
            rooms={project.rooms}
            receipts={receipts}
            readOnly={readOnly}
            onUpload={handleUploadReceipt}
            onUpdate={handleUpdateReceipt}
            onDelete={handleDeleteReceipt}
          />
        )}

        {/* Tasks & Notes View */}
        {activeView === 'tasks' && !isPublicAccess && (
          <ProjectTasksPanel projectId={project.id} isStaffMode={isStaffMode} />
        )}
      </div>

      <AddRoomDialog open={showAddRoom} onOpenChange={setShowAddRoom} onAdd={handleAddRoom} />

      <ConfirmDeleteDialog
        open={!!pendingItemDelete}
        onOpenChange={(o) => { if (!o) setPendingItemDelete(null); }}
        title={`Delete "${pendingItemDelete?.name || 'this item'}"?`}
        description="This item will be removed from the room. You'll have a moment to undo from the toast."
        destructiveLabel="Delete item"
        onConfirm={() => {
          if (pendingItemDelete) {
            const { roomId, itemId } = pendingItemDelete;
            setPendingItemDelete(null);
            handleDeleteItem(roomId, itemId);
          }
        }}
      />

      {project && (
        <ShareDialog
          open={showShare}
          onOpenChange={setShowShare}
          shareToken={project.share_token}
          viewToken={project.view_token}
        />
      )}

      {project && !isPublicAccess && !isStaffMode && (
        <BreakdownWizardDialog
          open={showBreakdown}
          onOpenChange={setShowBreakdown}
          project={project}
        />
      )}

      <DesignerProfileDialog
        open={showDesigner}
        onOpenChange={setShowDesigner}
        projectId={shareToken ? null : project?.id}
        shareToken={shareToken || null}
        readOnly={isViewMode || (isStaffMode && project?.can_edit === false)}
      />
    </div>
  );

  // In staff mode we slot the same body inside the staff chrome (sticky
  // emerald header on top, bottom nav). Everything else in the page is shared.
  if (isStaffMode) {
    return (
      <>
        <StaffHeader title={project.name} />
        {PageInner}
        <StaffBottomNav />
      </>
    );
  }
  return PageInner;
}
